import { Box, createStyles, FormControl, FormControlProps, FormHelperText, InputLabel, makeStyles, MenuItem, Select, Theme, Typography } from '@material-ui/core';
import { TextFieldProps } from '@material-ui/core/TextField';
import { ExpandMore as ExpandMoreIcon } from '@material-ui/icons';
import cn from 'classnames';
import { FormikProps } from 'formik';
import React from 'react';
import { CssSpreadType } from '../../utils/css-spread-type';

type Option = { label: string, value: any, description?:string | null };

export type IFormikSelectInput = FormControlProps & {
  label?: string;
  valueId: string;
  formik: FormikProps<any>;
  options: Option[];
  className?: string;
  classNameItem?: string;
  displayEmpty?: boolean;
  'data-test'?: string;
};

type IFormikSelectInputProps = TextFieldProps & IFormikSelectInput;
const FormikSelectInput: React.FC<IFormikSelectInputProps> = (props) => {
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const classes = useStyles();
  const {
    className,
    classNameItem,
    label = '',
    valueId,
    formik: { values, errors, handleChange, touched, handleBlur },
    options = [],
    displayEmpty,
    'data-test': dataTest,
    placeholder,
    ...other
  } = props;

  const error = !!errors[valueId] && !!touched[valueId];
  const value = ((values[valueId] || values[valueId] === 0) ? values[valueId] : '');

  return (
    <Box className={classes.root}>
      <FormControl
        name={dataTest || valueId}
        error={error}
        data-testid="formik-select-input__form-control"
        variant="outlined"
        className={cn(classes.formControl, className)}
        data-test={dataTest}
        placeholder={placeholder}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...other}
      >
        <InputLabel id={`label-${valueId}`}>
          {label}
        </InputLabel>
        <Select
          name={valueId}
          value={value}
          onChange={handleChange}
          labelId={`label-${valueId}`}
          onBlur={handleBlur}
          IconComponent={ExpandMoreIcon}
          displayEmpty={displayEmpty}
        >
          {displayEmpty && <MenuItem disabled value="">{placeholder}</MenuItem>}
          {options.map(({ value: ogValue, label: ogLabel, description }) => (
            <MenuItem
              data-testid="formik-select-input__item-option"
              id={`${dataTest || valueId}-${(ogLabel || ogValue || '').replace(/\s/g, '')}`}
              key={ogValue}
              value={ogValue}
              className={cn(classes.menuItem, description && classes.menuItemWithDescription)}
            >
              <Typography className={cn(description && classes.menuItemWithDescriptionTitle)}>
                {ogLabel.charAt(0).toUpperCase() + ogLabel.toLowerCase().slice(1)}
              </Typography>
              {description && (
                <Typography className={classes.menuItemWithDescriptionText}>
                  {description}
                </Typography>
              )}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>
          {error ? errors[valueId] : null}
        </FormHelperText>
      </FormControl>
    </Box>

  );
};

export default FormikSelectInput;

const useStyles = makeStyles((theme: Theme) => createStyles({
  root: {
    outline: 'none',
    width: '100%',
    '& .MuiInputLabel-formControl': {
      position: 'unset',
      transform: 'unset',
      marginBottom: '0.5em',
      ...theme.typography.body2,
      color: theme.palette.text4.main,
      '&.Mui-focused': {
        color: theme.palette.black.main,
      },
    },
    '& .MuiOutlinedInput-root': {
      '& legend span': {
        display: 'none',
      },
      '&.Mui-focused': {
        borderBottom: `solid 1px ${theme.palette.text5.main}`,
      },
    },
    '& .MuiOutlinedInput-input': {
      padding: '0.8em 1em',
      ...theme.typography.body2,
      color: theme.palette.text2.main,
    },
    '& .MuiFormHelperText-root': {
      marginLeft: theme.spacing(0),
    },
    '& .MuiOutlinedInput-notchedOutline': {
      top: 0,
      border: 'none',
      borderBottom: 'solid',
      borderColor: `${theme.palette.text5.main}!important`,
      borderWidth: '1px !important',
      borderRadius: '0 !important',
    },
    '&.Mui-focused': {
      color: theme.palette.black.main,
    },
    '&.MuiList-padding': {
      padding: 0,
    },
  },
  formControl: {
    minWidth: 190,
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
  menuItem: {
    '&:hover': {
      backgroundColor: theme.palette.bg4.default,
    },

    '&.Mui-selected': {
      backgroundColor: theme.palette.bg3.default,
    },
  },
  'MuiMenu-paper': {
    'MuiList-padding': {
      padding: 0,
    },
  },
  customLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  addAccount: {
    ...theme.typography.body2,
    color: theme.palette.primary.main,
    fontWeight: 600,
    minWidth: 'max-content',
  },
  menuItemWithDescription: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },
  menuItemWithDescriptionTitle: {
    ...theme.typography.body2,
    fontWeight: 600,
  },
  menuItemWithDescriptionText: {
    ...(theme.typography.small as CssSpreadType),
    color: theme.palette.text4.main,
    fontWeight: 400,
  },
  customLabelAccount: {
    margin: '0 !important',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
}));
